import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useMediaSettings, useUpdateMediaSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { ImageVariant } from '../../model/settingsTypes';
import {
  ChipListField,
  FeedbackSnackbar,
  NumberField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';

function ImageVariantsEditor({
  value,
  onChange,
  disabled,
}: {
  value: ImageVariant[];
  onChange: (value: ImageVariant[]) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  const update = (index: number, patch: Partial<ImageVariant>) =>
    onChange(value.map((variant, i) => (i === index ? { ...variant, ...patch } : variant)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  const add = () => onChange([...value, { name: '', width: null, height: null }]);
  const toNumber = (raw: string): number | null => (raw === '' ? null : Number(raw));

  return (
    <Stack spacing={1.5}>
      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('mediaSettings.variantsEditor.empty')}
        </Typography>
      ) : null}
      {value.map((variant, index) => (
        <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField
            size="small"
            label={t('mediaSettings.variantsEditor.nameLabel')}
            value={variant.name}
            disabled={disabled}
            onChange={(event) => update(index, { name: event.target.value })}
            sx={{ flexGrow: 1, width: '100%' }}
          />
          <TextField
            size="small"
            label={t('mediaSettings.variantsEditor.widthLabel')}
            type="number"
            value={variant.width ?? ''}
            disabled={disabled}
            onChange={(event) => update(index, { width: toNumber(event.target.value) })}
            sx={{ width: { xs: '100%', sm: 120 } }}
          />
          <TextField
            size="small"
            label={t('mediaSettings.variantsEditor.heightLabel')}
            type="number"
            value={variant.height ?? ''}
            disabled={disabled}
            onChange={(event) => update(index, { height: toNumber(event.target.value) })}
            sx={{ width: { xs: '100%', sm: 120 } }}
          />
          <IconButton
            aria-label={t('mediaSettings.variantsEditor.removeAria', { index: index + 1 })}
            onClick={() => remove(index)}
            disabled={disabled}
            color="error"
            size="small"
          >
            <DeleteOutlineRoundedIcon />
          </IconButton>
        </Stack>
      ))}
      <Box>
        <Button startIcon={<AddRoundedIcon />} onClick={add} disabled={disabled} size="small">
          {t('mediaSettings.variantsEditor.addVariant')}
        </Button>
      </Box>
    </Stack>
  );
}

/** Media settings: image rules, render variants, banners and CDN. */
export function MediaSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('media');
  const query = useMediaSettings();
  const update = useUpdateMediaSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('mediaSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('mediaSettings.title')}
      subtitle={t('mediaSettings.subtitle')}
      icon={<PermMediaRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('mediaSettings.uploadsSection.title')} icon={<ImageRoundedIcon />}>
              <ChipListField
                label={t('mediaSettings.uploadsSection.allowedFormatsLabel')}
                description={t('mediaSettings.uploadsSection.allowedFormatsDescription')}
                value={draft.allowedImageFormats}
                onChange={(value) => form.setField('allowedImageFormats', value)}
                disabled={disabled}
                options={['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg']}
              />
              <NumberField
                label={t('mediaSettings.uploadsSection.maxUploadSizeLabel')}
                value={draft.maxUploadSizeMb}
                onChange={(value) => form.setField('maxUploadSizeMb', value)}
                disabled={disabled}
                min={1}
                suffix={t('mediaSettings.uploadsSection.maxUploadSizeSuffix')}
              />
              <NumberField
                label={t('mediaSettings.uploadsSection.maxImagesLabel')}
                value={draft.maxImagesPerProduct}
                onChange={(value) => form.setField('maxImagesPerProduct', value)}
                disabled={disabled}
                min={1}
              />
            </SettingsCard>

            <SettingsCard
              title={t('mediaSettings.variantsSection.title')}
              description={t('mediaSettings.variantsSection.description')}
              icon={<PermMediaRoundedIcon />}
              divided={false}
            >
              <Box sx={{ py: 1 }}>
                <ImageVariantsEditor
                  value={draft.imageVariants}
                  onChange={(value) => form.setField('imageVariants', value)}
                  disabled={disabled}
                />
              </Box>
            </SettingsCard>

            <SettingsCard title={t('mediaSettings.deliverySection.title')} icon={<TuneRoundedIcon />}>
              <TextInputField
                label={t('mediaSettings.deliverySection.cdnBaseUrlLabel')}
                type="url"
                value={draft.cdnBaseUrl ?? ''}
                onChange={(value) => form.setField('cdnBaseUrl', value)}
                disabled={disabled}
                placeholder="https://cdn.example.com"
              />
              <SwitchField
                label={t('mediaSettings.deliverySection.autoOptimizeLabel')}
                description={t('mediaSettings.deliverySection.autoOptimizeDescription')}
                checked={draft.autoOptimizeEnabled}
                onChange={(value) => form.setField('autoOptimizeEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('mediaSettings.deliverySection.watermarkLabel')}
                checked={draft.watermarkEnabled}
                onChange={(value) => form.setField('watermarkEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('mediaSettings.deliverySection.bannersLabel')}
                description={t('mediaSettings.deliverySection.bannersDescription')}
                checked={draft.bannersEnabled}
                onChange={(value) => form.setField('bannersEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
